import { useState } from "react";
import { Plus, Edit2, Trash2, Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type Workspace,
  useDeleteCheck,
  useDeleteWorkspace,
  useWorkspaceAllowance,
  useWorkspaces,
} from "@/hooks/use-workspaces";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/lib/api-client";
import {
  canDelete,
  canRename,
  createRefusal,
  defaultWorkspace,
  limitMessage,
  ownedWorkspaces,
  siblingNames,
} from "@/utils/workspace-rules";
import { WorkspaceNameDialog } from "./WorkspaceNameDialog";

/** Which name dialog is open: a new workspace, or one being renamed. */
type NameDialog = { workspace?: Workspace } | null;

export function WorkspaceTab() {
  const { data: workspaces = [], isLoading } = useWorkspaces();
  const { currentWorkspaceId, setCurrentWorkspaceId } = useWorkspace();
  const deleteWorkspace = useDeleteWorkspace();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [nameDialog, setNameDialog] = useState<NameDialog>(null);
  const [deleting, setDeleting] = useState<Workspace | null>(null);
  const [deleteRefusal, setDeleteRefusal] = useState<string>();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const allowance = useWorkspaceAllowance();
  const deleteCheck = useDeleteCheck(deleting?.id);

  const owned = ownedWorkspaces(workspaces);
  // The plan's cap, as the server would answer a create: a free owner at theirs
  // is offered Pro, one at Pro's cap is told so — and nothing is sent. Until the
  // allowance is known the button waits; if it cannot be read, the server decides.
  const refusal = allowance.data ? createRefusal(allowance.data) : null;
  const handleNew = () => (refusal === "pro" ? setShowUpgradeDialog(true) : setNameDialog({}));
  // Delete is offered only once the server has said nothing blocks it.
  const deleteBlocked = deleteCheck.isLoading || !!deleteCheck.data?.message;

  const openDelete = (workspace: Workspace) => {
    setDeleteRefusal(undefined);
    setDeleting(workspace);
  };

  const handleDelete = async () => {
    const target = deleting!;
    setDeleteRefusal(undefined);
    try {
      await deleteWorkspace.mutateAsync(target.id);
    } catch (failure) {
      // A refusal (409), a role (403) or a workspace already gone (404) is a
      // sentence written for the owner; anything else is ours.
      setDeleteRefusal(
        failure instanceof ApiError && [403, 404, 409].includes(failure.status)
          ? failure.message
          : "The workspace could not be deleted. Try again.",
      );
      return;
    }
    toast({ title: "Workspace deleted", description: `${target.name} has been deleted.` });
    setDeleting(null);
    // Off the deleted workspace before any page reads from it again. There is
    // always another: delete is offered only to an owner of two or more.
    if (target.id === currentWorkspaceId) {
      setCurrentWorkspaceId(defaultWorkspace(workspaces.filter((w) => w.id !== target.id))!.id);
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeDialog(false);
    navigate(`/${currentWorkspaceId}/billing`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Workspaces</CardTitle>
              <CardDescription>Create and manage your workspaces</CardDescription>
            </div>
            <Button onClick={handleNew} disabled={allowance.isLoading || refusal === "limit"}>
              <Plus className="w-4 h-4 mr-2" />
              New Workspace
            </Button>
          </div>
          {refusal === "limit" && (
            <p className="text-sm text-muted-foreground">{limitMessage(allowance.data!.allowed)}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {workspaces.map((workspace) => (
              <div
                key={workspace.id}
                className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
              >
                <div className="flex-1">
                  <h3 className="font-medium">{workspace.name}</h3>
                  {workspace.id === currentWorkspaceId && (
                    <span className="text-xs text-primary">Current workspace</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canRename(workspace) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Rename ${workspace.name}`}
                      onClick={() => setNameDialog({ workspace })}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                  {workspace.role === "owner" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${workspace.name}`}
                      title={canDelete(workspace, owned.length) ? undefined : "Your only workspace can't be deleted"}
                      onClick={() => openDelete(workspace)}
                      disabled={!canDelete(workspace, owned.length)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {nameDialog && (
        <WorkspaceNameDialog
          workspace={nameDialog.workspace}
          siblings={siblingNames(workspaces, nameDialog.workspace)}
          onClose={() => setNameDialog(null)}
          onProRequired={() => {
            setNameDialog(null);
            setShowUpgradeDialog(true);
          }}
        />
      )}

      {/* Upgrade Required Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-4">
              <Crown className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="text-xl">Pro Feature</DialogTitle>
            <DialogDescription className="text-base">
              Creating multiple workspaces is available on the Pro plan. Upgrade to unlock this feature and get access to campaigns, 30 concurrent calls, and more.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-xl bg-muted/50 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Badge className="bg-primary">Pro</Badge>
                <span className="font-medium">$120/month</span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Multiple workspaces</li>
                <li>• Campaigns & batch calling</li>
                <li>• 30 concurrent calls</li>
                <li>• Priority support</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)} className="w-full sm:w-auto">
              Maybe Later
            </Button>
            <Button onClick={handleUpgrade} className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/90">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog */}
      <AlertDialog
        open={deleting !== null}
        // A delete in flight finishes in view.
        onOpenChange={(open) => !open && !deleteWorkspace.isPending && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the workspace and all
              associated agents, phone numbers, and call history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteCheck.isLoading && (
            <p className="text-sm text-muted-foreground">Checking whether it can be deleted…</p>
          )}
          {(deleteRefusal ?? deleteCheck.data?.message) && (
            <p role="alert" className="text-sm text-destructive">
              {deleteRefusal ?? deleteCheck.data?.message}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorkspace.isPending}>Cancel</AlertDialogCancel>
            {/* A plain button, not AlertDialogAction: that one closes the dialog
                on click, before a refusal could be shown in it. */}
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={deleteWorkspace.isPending || deleteBlocked}
            >
              {deleteWorkspace.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {deleteWorkspace.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
